/**
 * Vancouver (NLM/ICMJE) bibliography formatter.
 *
 * Rules implemented (ICMJE Recommendations):
 * - Author format: Surname AB (initials, no dots, no spaces)
 * - 1–6 authors: list all separated by commas; 7+ → first 6 + et al.
 * - Numbered in order of appearance in text (NOT alphabetical)
 * - Article: Smith AB, Jones CD. Title. J Abbrev. Year;Vol(Issue):Pages.
 * - No italic in plain text; journal title abbreviated (stored as-is in CSL)
 * - Optional DOI / PMID at the end
 *
 * Reference: ICMJE Uniform Requirements for Manuscripts Submitted to Biomedical Journals.
 */

import type {
    CslJson,
    ReferenceItem,
} from '@/components/references/ReferenceListItem.vue';

// ── Author helpers ────────────────────────────────────────────────────────────

interface CslName {
    family?: string;
    given?: string;
    literal?: string;
}

/** Convert "John Michael" → "JM" */
function givenToInitials(given: string): string {
    return given
        .trim()
        .split(/\s+/)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .filter(Boolean)
        .join('');
}

function formatVancouverAuthor(name: CslName): string {
    if (name.literal) {
        return name.literal;
    }

    const family = name.family ?? '';
    const initials = name.given ? givenToInitials(name.given) : '';

    return initials ? `${family} ${initials}` : family;
}

function formatVancouverAuthorList(authors: CslName[]): string {
    if (!authors.length) {
        return '';
    }

    if (authors.length <= 6) {
        return authors.map(formatVancouverAuthor).join(', ');
    }

    return (
        authors.slice(0, 6).map(formatVancouverAuthor).join(', ') + ', et al.'
    );
}

// ── Plain-text fallback ───────────────────────────────────────────────────────

function plainToVancouver(fullName: string): string {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length < 2) {
        return fullName;
    }

    const family = parts[parts.length - 1];
    const initials = parts
        .slice(0, -1)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');

    return `${family} ${initials}`;
}

function formatFallbackAuthorList(authors: string[]): string {
    if (!authors.length) {
        return '';
    }

    if (authors.length <= 6) {
        return authors.map(plainToVancouver).join(', ');
    }

    return authors.slice(0, 6).map(plainToVancouver).join(', ') + ', et al.';
}

// ── Year extraction ───────────────────────────────────────────────────────────

function cslYear(csl: CslJson): string {
    const y = csl.issued?.['date-parts']?.[0]?.[0];

    return y && Number(y) > 0 ? String(y) : '';
}

/** Returns "${authorStr}. " or "" when author is empty (avoids leading ". "). */
function authorPrefix(authorStr: string): string {
    return authorStr ? `${authorStr}. ` : '';
}

// ── Per-type formatters ───────────────────────────────────────────────────────

function formatArticleJournal(csl: CslJson, number: number): string {
    const authorStr = formatVancouverAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const journal = csl['container-title'] ?? '';
    const vol = csl.volume ?? '';
    const issue = csl.issue ? `(${csl.issue})` : '';
    const pages = csl.page ?? '';
    const doi = csl.DOI ? ` doi: ${csl.DOI}` : '';

    // Vancouver date+vol+issue: Year;Vol(Issue):Pages
    let locator = year;

    if (vol) {
        locator += locator ? `;${vol}${issue}` : vol + issue;
    }

    if (pages) {
        locator += locator ? `:${pages}` : pages;
    }

    return `${number}. ${authorPrefix(authorStr)}${title}. ${journal}. ${locator}.${doi}`;
}

function formatBook(csl: CslJson, number: number): string {
    const authorStr = formatVancouverAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const edition = csl.edition ? ` ${csl.edition} ed.` : '';
    const place = csl['publisher-place'] ?? '';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` doi: ${csl.DOI}` : '';

    const pubStr = [place, publisher].filter(Boolean).join(': ');
    const yearSuffix = year ? `; ${year}` : '';

    return `${number}. ${authorPrefix(authorStr)}${title}${edition}. ${pubStr}${yearSuffix}.${doi}`;
}

function formatChapter(csl: CslJson, number: number): string {
    const authorStr = formatVancouverAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const bookTitle = csl['container-title'] ?? '';
    const editors = (csl.editor ?? []).map(formatVancouverAuthor).join(', ');
    const editorStr = editors ? `, editors. ` : '';
    const pages = csl.page ? `p. ${csl.page}. ` : '';
    const place = csl['publisher-place'] ?? '';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` doi: ${csl.DOI}` : '';

    const pubStr = [place, publisher].filter(Boolean).join(': ');
    const yearSuffix = year ? `; ${year}` : '';

    return (
        `${number}. ${authorPrefix(authorStr)}${title}. In: ${editors}${editorStr}${bookTitle}. ${pubStr}${yearSuffix}. ${pages}${doi}`.trim() +
        '.'
    );
}

function formatThesis(csl: CslJson, number: number): string {
    const authorStr = formatVancouverAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const genre = csl.genre ? `[${csl.genre}]` : '[Thesis]';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` doi: ${csl.DOI}` : '';
    const url = !csl.DOI && csl.URL ? ` Available from: ${csl.URL}` : '';

    const yearSuffix = year ? `; ${year}` : '';

    return `${number}. ${authorPrefix(authorStr)}${title} ${genre}. ${publisher}${yearSuffix}.${doi}${url}`;
}

function formatWebpage(csl: CslJson, number: number): string {
    const authorStr = formatVancouverAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const url = csl.URL ? ` Available from: ${csl.URL}` : '';
    const siteName = csl['container-title']
        ? csl['container-title'] + '. '
        : '';

    return `${number}. ${authorPrefix(authorStr)}${title} [Internet]. ${siteName}${year}${url}`;
}

// ── Fallback ──────────────────────────────────────────────────────────────────

function formatFallback(ref: ReferenceItem, number: number): string {
    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const authorStr = formatFallbackAuthorList(authors.filter(Boolean));
    const year = ref.year && ref.year > 0 ? String(ref.year) : '';
    const doi = ref.doi ? ` doi: ${ref.doi}` : '';
    const yearStr = year ? ` ${year}.` : '';

    return `${number}. ${authorPrefix(authorStr)}${ref.title}.${yearStr}${doi}`;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Format a single reference in Vancouver bibliography style.
 * `number` = 1-based position in order of appearance.
 */
export function formatVancouverBibliography(
    ref: ReferenceItem,
    number: number,
): string {
    const csl = ref.csl;

    if (!csl) {
        return formatFallback(ref, number);
    }

    switch (csl.type) {
        case 'article-journal':
        case 'article-magazine':
        case 'article-newspaper':
            return formatArticleJournal(csl, number);
        case 'book':
            return formatBook(csl, number);
        case 'chapter':
            return formatChapter(csl, number);
        case 'thesis':
            return formatThesis(csl, number);
        case 'webpage':
        case 'post-weblog':
        case 'online':
            return formatWebpage(csl, number);
        default:
            return csl['container-title']
                ? formatArticleJournal(csl, number)
                : formatBook(csl, number);
    }
}

/**
 * Vancouver keeps references in order of appearance (caller provides the ordered list).
 * This function is a no-op sort (order preserved) provided for API symmetry.
 */
export function sortVancouver(refs: ReferenceItem[]): ReferenceItem[] {
    return refs; // order of appearance is preserved by caller
}

/**
 * Format the inline Vancouver citation label.
 * Returns "[N]" where N is the 1-based order-of-appearance index.
 */
export function formatVancouverInline(
    _ref: ReferenceItem,
    index: number,
): string {
    return `[${index}]`;
}
